import { FieldListDto } from "@common/dto/field/list-dto";
import { generateId } from "@common/utils/common";
import React from "react";
import {
  PedigreeSettingsFieldsManagementProps,
  ProcessedFieldListDto,
} from "./fields-management.types";

import { PedigreeSettingsFormatManagementFieldDelimiters } from "@common/dto/pedigree/format-field-delimiters";
import { FolderListDto } from "@common/dto/folder/list-dto";

export const useController = (props: PedigreeSettingsFieldsManagementProps) => {
  const { config, value } = props;

  const [selectedFields, setSelectedFields] = React.useState<
    Array<ProcessedFieldListDto>
  >([]);

  const [selectedField, setSelectedField] = React.useState<
    ProcessedFieldListDto | undefined
  >();
  const [fieldChooserOpened, setFieldChooserOpened] = React.useState(false);

  React.useEffect(() => {
    let newValue: Array<ProcessedFieldListDto> = value?.length
      ? (value.map((field) => {
          field.internalUniqId = generateId();
          return field;
        }) as Array<ProcessedFieldListDto>)
      : ([
          {
            internalUniqId: generateId(),
            field: {} as FieldListDto,
            folder: {} as FolderListDto,
          },
        ] as Array<ProcessedFieldListDto>);
    setSelectedFields(newValue);
  }, [value]);

  const delimiterOptions = React.useMemo(() => {
    return [
      {
        value: PedigreeSettingsFormatManagementFieldDelimiters.space,
        label: "Space ( )",
      },
      {
        value: PedigreeSettingsFormatManagementFieldDelimiters.comma,
        label: "Comma (, )",
      },
      {
        value: PedigreeSettingsFormatManagementFieldDelimiters.semiColon,
        label: "Semi-colon (; )",
      },
    ];
  }, []);

  const openFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(true);
  }, []);

  const closeFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(false);
    setSelectedField(undefined);
  }, [selectedFields]);

  const handleDelimiterOptionSelect = (
    field: ProcessedFieldListDto,
    selectedValue: PedigreeSettingsFormatManagementFieldDelimiters
  ) => {
    let newselected = selectedFields.map((selectedField) => {
      if (selectedField.internalUniqId == field.internalUniqId) {
        selectedField.delimiter = selectedValue;
        return selectedField;
      }
      return selectedField;
    });
    setSelectedFields(newselected);

    config.submitProps.submit({
      fields: selectedFields.map(({ internalUniqId, ...rest }) => rest),
    });
  };

  const handleFieldSelect = ({
    field,
    folder,
  }: {
    field: FieldListDto;
    folder: FolderListDto;
  }) => {
    const updateExistingField = !!selectedField?.field;

    setSelectedFields((prev) => {
      if (updateExistingField) {
        const newValue = prev.map((item) => {
          if (item.internalUniqId === selectedField?.internalUniqId) {
            item.folder = folder;
            item.field = field;
            item.fieldName = field.fieldName;
            item.fieldId = field.fieldId;
            item.systemfield=field.systemField
          }
          return item;
        });
        return newValue;
      } else {
        const newField = {
          internalUniqId: generateId(),
          field,
          folder,
          fieldName: field.fieldName,
          fieldId: field.fieldId,
        };
        prev.push(newField);
        return prev;
      }
    });

    closeFieldChooser();
    config.submitProps.submit({
      fields: selectedFields.map(({ internalUniqId, ...rest }) => rest),
    });
  };

  const handleFieldRemove = (field: ProcessedFieldListDto) => {
    let newSelectedFields = selectedFields.filter(
      (selectedField) => selectedField.internalUniqId !== field.internalUniqId
    );
    setSelectedFields(newSelectedFields);
    config.submitProps.submit({
      fields: newSelectedFields.map(({ internalUniqId, ...rest }) => rest),
    });
  };

  const handleSelectedFieldChange = React.useCallback(
    (field: ProcessedFieldListDto) => {
      setSelectedField(field);
      openFieldChooser();
    },
    [openFieldChooser]
  );

  return {
    selectedFields,
    fieldChooserOpened,
    delimiterOptions,
    openFieldChooser,
    closeFieldChooser,
    handleDelimiterOptionSelect,
    handleSelectedFieldChange,
    handleFieldSelect,
    handleFieldRemove,
    selectedField,
    setSelectedFields,
  };
};
